import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { Toaster } from '@/components/ui/sonner';
import { useAuth } from "@/contexts/AuthContext";

// Layout and Protected Routes
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedRouteV2 from './components/ProtectedRouteV2';

// Import Static Routes
import AdminRoutes from './routes/AdminRoutes';

// Pages with direct imports for core functionality
import NotFound from './pages/NotFound';
import Auth from './pages/Auth';
import Index from './pages/Index';
import CrmDashboardPage from './pages/CrmDashboardPage'; // New CRM Dashboard
import LoadingPage from './pages/LoadingPage';

// Page imports for static routes
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const ContactEdit = lazy(() => import('./pages/ContactEdit'));
const NewContactEditor = lazy(() => import('./pages/NewContactEditor'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const CampaignDetail = lazy(() => import('./pages/CampaignDetail'));
const Companies = lazy(() => import('./pages/Companies'));
const CompanyDetail = lazy(() => import('./pages/CompanyDetail'));
const CompanyEdit = lazy(() => import('./pages/CompanyEdit'));
const CompanyNew = lazy(() => import('./pages/CompanyNew'));
const Clients = lazy(() => import('./pages/Clients'));
const ClientDetail = lazy(() => import('./pages/ClientDetail'));
const ClientEdit = lazy(() => import('./pages/ClientEdit'));
const ClientNew = lazy(() => import('./pages/ClientNew'));
const Opportunities = lazy(() => import('./pages/Opportunities'));
const OpportunityNew = lazy(() => import('./pages/OpportunityNew'));
const OpportunityEdit = lazy(() => import('./pages/OpportunityEdit'));
const OpportunityDetail = lazy(() => import('./pages/OpportunityDetail'));
const Views = lazy(() => import('./pages/Views'));
const ContactsSearch = lazy(() => import('./pages/ContactsSearch'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const RoleManager = lazy(() => import('./pages/RoleManager'));
const CompanyLinkingPage = lazy(() => import('./pages/CompanyLinkingPage'));
const BaseAssignmentsPage = lazy(() => import('./pages/BaseAssignmentsPage'));
const Replies = lazy(() => import('./pages/Replies'));
const Industries = lazy(() => import('./pages/Industries'));
const IndustryDetail = lazy(() => import('./pages/IndustryDetail'));
const IndustryNew = lazy(() => import('./pages/IndustryNew'));
const IndustryEdit = lazy(() => import('./pages/IndustryEdit'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const SupplierDetail = lazy(() => import('./pages/SupplierDetail'));
const SupplierNew = lazy(() => import('./pages/SupplierNew'));
const SupplierEdit = lazy(() => import('./pages/SupplierEdit'));
const Gmail = lazy(() => import('./pages/Gmail'));
const GmailAuthCallback = lazy(() => import('./pages/GmailAuthCallback'));
const Meetings = lazy(() => import('./pages/Meetings'));
const MeetingEdit = lazy(() => import('./pages/MeetingEdit'));
const MeetingDetail = lazy(() => import('./pages/MeetingDetail'));
const MeetingNew = lazy(() => import('./pages/MeetingNew'));
const Events = lazy(() => import('./pages/Events'));
const EventNew = lazy(() => import('./pages/EventNew'));
const EventEdit = lazy(() => import('./pages/EventEdit'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const TasksExpandedView = lazy(() => import('./pages/TasksExpandedView')); // Keep this for /tasks
const TaskDetail = lazy(() => import('./pages/TaskDetail'));
const TaskNew = lazy(() => import('./pages/TaskNew'));
const TaskEdit = lazy(() => import('./pages/TaskEdit'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const ProjectNew = lazy(() => import('./pages/ProjectNew'));
const ProjectEdit = lazy(() => import('./pages/ProjectEdit'));
const Leads = lazy(() => import('./pages/Leads'));
const LeadNew = lazy(() => import('./pages/LeadNew'));
const LeadDetail = lazy(() => import('./pages/LeadDetail'));
const LeadEdit = lazy(() => import('./pages/LeadEdit'));
const Intros = lazy(() => import('./pages/Intros'));
const IntroDetail = lazy(() => import('./pages/IntroDetail'));
const IntroEdit = lazy(() => import('./pages/IntroEdit'));
const IntroNew = lazy(() => import('./pages/IntroNew'));
const NewIntroDetail = lazy(() => import('./pages/NewIntroDetail'));
const NewIntroEdit = lazy(() => import('./pages/NewIntroEdit'));
const News = lazy(() => import('./pages/News'));
const RegistrationsPage = lazy(() => import('./pages/RegistrationsPage'));
const RegistrationNewPage = lazy(() => import('./pages/RegistrationNewPage'));
const SmsDemo = lazy(() => import('./pages/SmsDemo'));
const ResendTest = lazy(() => import('./pages/ResendTest'));
const Investments = lazy(() => import('./pages/Investments'));
const InvestmentDetail = lazy(() => import('./pages/InvestmentDetail'));
const InvestmentForm = lazy(() => import('./pages/InvestmentForm'));
const InvestmentEdit = lazy(() => import('./pages/InvestmentEdit'));
const IntrosDashboard = lazy(() => import('./pages/IntrosDashboard'));
const TestMapbox = lazy(() => import('./pages/TestMapbox'));
const TestApollo = lazy(() => import('./pages/TestApollo'));

// Dynamic route generation utilities
import { fetchAllRoutes, RouteItem } from './utils/routeUtils';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Dynamic component import function - Fixed to include file extension
const importPage = (componentName: string) => {
  return lazy(() => {
    // Add .tsx extension to import path
    return import(`./pages/${componentName}.tsx`)
      .catch(err => {
        console.error(`Failed to load component: ${componentName}`, err);
        return import('./pages/NotFound');
      });
  });
};

function DynamicRoutes() {
  const [routes, setRoutes] = useState<RouteItem[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadRoutes = async () => {
      try {
        const allRoutes = await fetchAllRoutes();
        setRoutes(allRoutes);
      } catch (error) {
        console.error("Failed to load routes:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadRoutes();
  }, []);
  
  if (loading) {
    return <LoadingPage message="Loading routes..." />;
  }
  
  return (
    <Routes>
      {/* Authentication route */}
      <Route path="/auth" element={<Auth />} />
      
      {/* Public home page */}
      <Route path="/" element={<Index />} />
      
      {/* Protected Dashboard Route for authenticated users */}
      <Route path="/dashboard" element={
        <ProtectedRouteV2 path="/dashboard">
          <CrmDashboardPage /> {/* Use new CRM Dashboard */}
        </ProtectedRouteV2>
      } />

      {/* Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRouteV2 path="/admin">
          <AdminRoutes />
        </ProtectedRouteV2>
      } />
      
      {/* Dynamically generated routes from database */}
      {routes.filter(route => route.isRouteEnabled && route.componentName && route.path !== '/dashboard').map((route) => { // Exclude /dashboard as it's handled above
        const Component = importPage(route.componentName);
        
        return (
          <Route 
            key={route.id}
            path={route.path}
            element={
              <ProtectedRouteV2 path={route.path}>
                <Suspense fallback={<LoadingPage />}>
                  <Component />
                </Suspense>
              </ProtectedRouteV2>
            }
          />
        );
      })}
      
      {/* Fallback routes */}
      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}

// Legacy AppRoutes for backward compatibility
// The original App.tsx contains a lot of static routes
// We'll keep this for now and gradually migrate to dynamic routes
function AppRoutes() {
  const { user, isAdmin } = useAuth();
  
  return (
    <Routes>
      <Route path="/auth" element={<Auth />} />
      
      {/* Public home page */}
      <Route path="/" element={<Index />} />
      
      {/* Protected Dashboard Route for authenticated users */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <CrmDashboardPage /> {/* Use new CRM Dashboard */}
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute isAdmin>
          <AdminRoutes />
        </ProtectedRoute>
      } />
      
      <Route path="/contact/:id" element={<ContactDetail />} />
      <Route path="/connections/:id" element={<ContactDetail />} /> {/* Add support for legacy links */}
      <Route path="/contact/:id/edit" element={<ContactEdit />} />
      <Route path="/contact/new" element={<ContactEdit />} />
      <Route path="/contact/new-editor" element={<NewContactEditor />} />
      <Route path="/contact/new-editor/:id" element={<NewContactEditor />} />
      <Route path="/campaigns" element={<Campaigns />} />
      <Route path="/campaign/:id" element={<CampaignDetail />} />
      <Route path="/companies" element={<Companies />} />
      <Route path="/company/:id" element={<CompanyDetail />} />
      <Route path="/company/:id/edit" element={<CompanyEdit />} />
      <Route path="/company/new" element={<CompanyNew />} />
      
      {/* Client Routes */}
      <Route path="/clients" element={<Clients />} />
      <Route path="/client/:id" element={<ClientDetail />} />
      <Route path="/client/:id/edit" element={<ClientEdit />} />
      <Route path="/client/new" element={<ClientNew />} />
      
      <Route path="/opportunities" element={<Opportunities />} />
      <Route path="/opportunity/new" element={<OpportunityNew />} />
      <Route path="/opportunity/edit/:id" element={<OpportunityEdit />} />
      <Route path="/opportunities/:id" element={<OpportunityDetail />} />
      <Route path="/dashboards" element={<Navigate to="/dashboard" replace />} /> {/* Ensure this also points to new dashboard */}
      <Route path="/views" element={<Views />} />
      <Route path="/contacts2" element={<ContactsSearch />} />
      
      <Route path="/admin/users" element={
        <ProtectedRoute isAdmin>
          <AdminUsers />
        </ProtectedRoute>
      } />
      
      <Route path="/role-manager" element={
        <ProtectedRoute isAdmin>
          <RoleManager />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/company-linking" element={
        <ProtectedRoute isAdmin>
          <CompanyLinkingPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/base-assignments" element={
        <ProtectedRoute isAdmin>
          <BaseAssignmentsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/replies" element={<Replies />} />
      <Route path="/industries" element={<Industries />} />
      <Route path="/industry/:id" element={<IndustryDetail />} />
      <Route path="/industry/new" element={<IndustryNew />} />
      <Route path="/industry/:id/edit" element={<IndustryEdit />} />
      <Route path="/suppliers" element={<Suppliers />} />
      <Route path="/supplier/:id" element={<SupplierDetail />} />
      <Route path="/supplier/new" element={<SupplierNew />} />
      <Route path="/supplier/:id/edit" element={<SupplierEdit />} />
      <Route path="/gmail" element={<Gmail />} />
      <Route path="/gmail/auth/callback" element={<GmailAuthCallback />} />
      <Route path="/meetings" element={<Meetings />} />
      <Route path="/meeting/:id/edit" element={<MeetingEdit />} />
      <Route path="/meeting/:id" element={<MeetingDetail />} />
      <Route path="/meeting/new" element={<MeetingNew />} />
      
      <Route path="/events" element={<Events />} />
      <Route path="/events/new" element={<EventNew />} />
      <Route path="/events/edit/:id" element={<EventEdit />} />
      <Route path="/events/:id" element={<EventDetail />} />
      
      <Route path="/task/:id" element={<TaskDetail />} />
      <Route path="/tasks/:id" element={<TaskDetail />} />
      <Route path="/task/new" element={<TaskNew />} />
      <Route path="/task/:id/edit" element={<TaskEdit />} />
      <Route path="/tasks/:id/edit" element={<TaskEdit />} />
      {/* Updated Task Route to point to TasksExpandedView */}
      <Route path="/tasks" element={<TasksExpandedView />} />
      
      {/* Project Routes */}
      <Route path="/projects" element={<Projects />} />
      <Route path="/project/:id" element={<ProjectDetail />} />
      <Route path="/project/new" element={<ProjectNew />} />
      <Route path="/project/:id/edit" element={<ProjectEdit />} />
      
      <Route path="/leads" element={<Leads />} />
      <Route path="/leads/new" element={<LeadNew />} />
      <Route path="/leads/:id" element={<LeadDetail />} />
      <Route path="/leads/:id/edit" element={<LeadEdit />} />
      
      <Route path="/intros" element={<Intros />} />
      <Route path="/intros/:id" element={<IntroDetail />} />
      <Route path="/intros/:id/edit" element={<IntroEdit />} />
      <Route path="/intros/new" element={<IntroNew />} />
      <Route path="/intros/new/:id" element={<NewIntroDetail />} />
      <Route path="/intros/new/:id/edit" element={<NewIntroEdit />} />
      
      <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
      <Route path="/admin2" element={<Navigate to="/admin/users" replace />} />
      
      <Route path="/news" element={<News />} />
      
      <Route path="/registrations" element={<RegistrationsPage />} />
      <Route path="/registrations/new" element={<RegistrationNewPage />} />
      
      <Route path="/sms" element={<SmsDemo />} />
      
      <Route path="/resend-test" element={<ResendTest />} />
      
      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
      
      <Route path="/investments" element={<Investments />} />
      <Route path="/investments/:id" element={<InvestmentDetail />} />
      <Route path="/investments/new" element={<InvestmentForm />} />
      <Route path="/investments/:id/edit" element={<InvestmentEdit />} />
      
      <Route path="/intros-dashboard" element={<IntrosDashboard />} />
      
      <Route path="/test_mapbox" element={<TestMapbox />} />
      
      {/* Add the TestApollo route */}
      <Route path="/test_apollo" element={<TestApollo />} />
    </Routes>
  );
}

function App() {
  console.log("App component rendering");
  const [useDynamicRoutes, setUseDynamicRoutes] = useState(true); // Default to true to use DynamicRoutes
  
  // Check if we should use dynamic routes
  useEffect(() => {
    const checkDynamicRoutes = async () => {
      try {
        const shouldUseDynamic = localStorage.getItem('useDynamicRoutes');
        if (shouldUseDynamic === null) { // If not set, default to true
            setUseDynamicRoutes(true);
            localStorage.setItem('useDynamicRoutes', 'true');
        } else {
            setUseDynamicRoutes(shouldUseDynamic === 'true');
        }
      } catch (error) {
        console.error("Error checking dynamic routes:", error);
        setUseDynamicRoutes(true); // Fallback to true on error
      }
    };
    
    checkDynamicRoutes();
  }, []);
  
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Layout>
          {useDynamicRoutes ? <DynamicRoutes /> : <AppRoutes />}
        </Layout>
        <Toaster position="top-right" />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
